import { useRef, useState, useEffect } from 'react';
import { Rocket, Cpu, Shield, Code2, Vote } from 'lucide-react';
import { useTr } from '../contexts/LanguageContext';

const useInView = (threshold = 0.08) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const EcosystemSection = () => {
  const t = useTr();
  const { ref, inView } = useInView();

  const participants = [
    {
      id: 'deployers', icon: Rocket, color: '#30d5c8',
      title: t('Swarm Deployers', '스웜 배포자'),
      desc: t('Companies and developers who launch and orchestrate AI agent swarms for enterprise automation, research, and product workflows.', '기업 자동화, 연구, 제품 워크플로우를 위한 AI 에이전트 스웜을 구동하는 기업 및 개발자입니다.'),
      stats: [t('Any scale, any use case', '모든 규모에 적용'), t('Deploy via SDK or platform', 'SDK 또는 플랫폼으로 배포')],
    },
    {
      id: 'compute-providers', icon: Cpu, color: '#55d532',
      title: t('Compute Providers', '컴퓨팅 제공자'),
      desc: t('Contribute GPU compute to the network and receive $SWARM as compensation for verified compute work.', '네트워크에 GPU 컴퓨팅을 기여하고 검증된 컴퓨팅 작업에 대해 $SWARM으로 보상을 받습니다.'),
      stats: [t('Compensation for verified work', '검증된 작업에 대한 보상'), t('No minimum hardware', '최소 하드웨어 요구 없음')],
    },
    {
      id: 'validators', icon: Shield, color: '#4de8d0',
      title: t('Validators', '검증인'),
      desc: t('Post $SWARM as collateral and verify agent outputs and swarm proofs, earning protocol rewards for verification work performed.', '$SWARM을 담보로 예치하고 에이전트 출력물 및 스웜 증명을 검증하여, 수행한 검증 작업에 대해 프로토콜 보상을 받습니다.'),
      stats: [t('Collateral-based participation', '담보 기반 참여'), t('Rewards for work performed', '수행 작업에 대한 보상')],
    },
    {
      id: 'developers', icon: Code2, color: '#27d555',
      title: t('Developers', '개발자'),
      desc: t('Build agent templates, swarm frameworks, and integrations on the SwarmBase SDK. SDK grants available from the Ecosystem allocation.', 'SwarmBase SDK로 에이전트 템플릿, 스웜 프레임워크 및 통합 솔루션을 개발합니다. 생태계 배분에서 SDK 지원금이 제공됩니다.'),
      stats: [t('SDK grants available', 'SDK 지원금 제공'), t('Open contributions welcome', '오픈 기여 환영')],
    },
    {
      id: 'dao', icon: Vote, color: '#7bed9f',
      title: t('DAO Governance', 'DAO 거버넌스'),
      desc: t('$SWARM holders govern protocol parameters, treasury allocation, and ecosystem grants via fully on-chain governance.', '$SWARM 보유자가 프로토콜 파라미터, 재무 배분, 생태계 지원금을 완전한 온체인 거버넌스로 결정합니다.'),
      stats: [t('1 token = 1 vote', '1 토큰 = 1 투표'), t('Fully on-chain governance', '완전한 온체인 거버넌스')],
    },
  ];

  return (
    <section id="ecosystem" className="py-20 sm:py-24 md:py-32 dot-grid"
      style={{ position: 'relative', zIndex: 10, background: 'var(--sb-bg-3)' }}
      data-testid="ecosystem-section">
      <div style={{ position: 'absolute', top: '30%', right: '-10%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(48,213,200,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">
        <div ref={ref} className={`mb-12 md:mb-16 anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">{t('Ecosystem', '생태계')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tight">
            {t('The SwarmBase', 'SwarmBase')}<br />
            <span className="gradient-text">{t('Network Participants', '네트워크 참여자')}</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl text-sm md:text-base">
            {t('A network of deployers, compute providers, validators, developers, and governance participants, coordinated by the $SWARM token.',
               '배포자, 컴퓨팅 제공자, 검증인, 개발자, 거버넌스 참여자로 구성된 네트워크입니다. $SWARM 토큰으로 연결됩니다.')}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {participants.slice(0, 3).map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.id} data-testid={`ecosystem-card-${p.id}`}
                className={`glass card-3d animated-border rounded-2xl p-5 sm:p-6 flex flex-col gap-4 anim-hidden ${inView ? 'anim-visible' : ''}`}
                style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ background: `${p.color}12`, border: `1px solid ${p.color}35` }}>
                  <Icon size={18} color={p.color} />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-white text-base sm:text-lg mb-2">{p.title}</h3>
                  <p className="text-white/55 text-xs sm:text-sm leading-relaxed">{p.desc}</p>
                </div>
                <div className="mt-auto pt-3 border-t border-white/5 flex flex-wrap gap-2">
                  {p.stats.map(s => <span key={s} className="text-xs font-mono px-2 py-1 rounded" style={{ color: `${p.color}cc`, background: `${p.color}08` }}>{s}</span>)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-4 sm:mt-5 lg:max-w-[66.67%] lg:mx-auto">
          {participants.slice(3).map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.id} data-testid={`ecosystem-card-${p.id}`}
                className={`glass card-3d animated-border rounded-2xl p-5 sm:p-6 flex flex-col gap-4 anim-hidden ${inView ? 'anim-visible' : ''}`}
                style={{ transitionDelay: `${(i + 3) * 0.08}s` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}12`, border: `1px solid ${p.color}35` }}>
                  <Icon size={18} color={p.color} />
                </div>
                <div>
                  <h3 className="font-outfit font-bold text-white text-base sm:text-lg mb-2">{p.title}</h3>
                  <p className="text-white/55 text-xs sm:text-sm leading-relaxed">{p.desc}</p>
                </div>
                <div className="mt-auto pt-3 border-t border-white/5 flex flex-wrap gap-2">
                  {p.stats.map(s => <span key={s} className="text-xs font-mono px-2 py-1 rounded" style={{ color: `${p.color}cc`, background: `${p.color}08` }}>{s}</span>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EcosystemSection;
